<template>
  <yams-template>
    <template #header>
      <div class="mr-5">
        <h4 class="text-primary">Tableau de bord</h4>
      </div>
      <div class="ml-auto">
        <b-form-select v-model="selectedPeriode" :options="periodes.options" class="mb-3" size="sm"></b-form-select>
      </div>
    </template>
    <template #content> <!-- KPI Section -->
      <div class="row mb-4">
        <div class="col-md-3 mb-4 mb-md-0">
          <kpi-card label="Courses" :value="formatNumber(kpis.coursesCount)" icon="fas fa-taxi"
            :loading="isLoading('kpis')" color-theme="primary" :trend="5" />
        </div>
        <div class="col-md-3 mb-4 mb-md-0">
          <kpi-card label="Chiffre d'affaires" :value="formatCurrency(kpis.chiffreAffaires)"
            icon="fa-solid fa-money-bill-wave" :loading="isLoading('kpis')" color-theme="success" :trend="8.2" />
        </div>

        <div class="col-md-3 mb-4 mb-md-0">
          <kpi-card label="Clients" :value="formatNumber(kpis.clientsCount)" icon="fas fa-users"
            :loading="isLoading('kpis')" color-theme="info" :trend="3.5" />
        </div>
        <div class="col-md-3">
          <kpi-card label="Chauffeurs" :value="formatNumber(kpis.driversCount)" icon="fas fa-id-card"
            :loading="isLoading('kpis')" color-theme="warning" :trend="0" />
        </div>
      </div> <!-- Charts Section -->
      <div class="row mb-4">
        <div class="col-lg-8 mb-4 mb-lg-0">
          <line-chart title="Évolution du chiffre d'affaires" :chart-data="chiffreAffairesChartData"
            :loading="isLoading('chiffreAffaires')" />
        </div>
        <div class="col-lg-4">
          <gauge-chart title="Objectif mensuel" :value="tauxObjectif" centerLabel="du CA mensuel" />
        </div>
      </div>

      <div class="row mb-4">
        <div class="col-lg-8 mb-4 mb-lg-0">
          <bar-chart title="Nombre de courses par jour" :chart-data="coursesCountChartData"
            :loading="isLoading('coursesCount')" />
        </div>
        <div class="col-lg-4">
          <pie-chart title="Répartition des courses par type" :chart-data="repartitionTypeChartData"
            :loading="isLoading('repartition')" />
        </div>
      </div>

      <div class="row mb-4">
        <div class="col-lg-6 mb-4 mb-lg-0">
          <stacked-bar-chart title="Répartition des courses par statut" :chart-data="repartitionStatusChartData"
            :loading="isLoading('repartition')" />
        </div>
        <div class="col-lg-6">
          <pie-chart title="Répartition par moyen de paiement" :chart-data="paymentMethodChartData"
            :loading="isLoading('repartition')" />
        </div>
      </div>

      <div class="row">
        <div class="col-lg-6 mb-4 mb-lg-0">
          <top-clients-bar-chart title="Top 10 Clients" :clients="topClients" :loading="isLoading('topClients')" />
        </div>
        <div class="col-lg-6">
          <top-drivers-bar-chart title="Top 10 Chauffeurs" :drivers="topDrivers" :loading="isLoading('topDrivers')" />
        </div>
      </div>
    </template>
  </yams-template>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import KpiCard from './statistiques/KpiCard.vue';
import LineChart from './statistiques/LineChart.vue';
import BarChart from './statistiques/BarChart.vue';
import PieChart from './statistiques/PieChart.vue';
import StackedBarChart from './statistiques/StackedBarChart.vue';
import GaugeChart from './statistiques/GaugeChart.vue';
import TopClientsBarChart from './statistiques/TopClientsBarChart.vue';
import TopDriversBarChart from './statistiques/TopDriversBarChart.vue';

export default {
  name: 'TableauDeBord',
  components: {
    KpiCard,
    LineChart,
    BarChart,
    PieChart,
    StackedBarChart,
    GaugeChart,
    TopClientsBarChart,
    TopDriversBarChart
  },
  data() {
    return {
      tauxObjectif: 75, // Exemple de pourcentage d'objectif atteint
      paymentMethods: [
        { method: 'Carte bancaire', count: 120, color: '#4e73df' },
        { method: 'Espèces', count: 80, color: '#1cc88a' },
        { method: 'Compte client', count: 60, color: '#36b9cc' },
        { method: 'Autre', count: 20, color: '#f6c23e' }
      ]
    };
  },
  computed: {
    ...mapGetters('statistiques', [
      'kpis',
      'chiffreAffaires',
      'coursesCount',
      'topClients',
      'topDrivers',
      'repartitionByType',
      'repartitionByStatus',
      'periodes'
    ]),

    selectedPeriode: {
      get() {
        return this.$store.getters['statistiques/selectedPeriode'];
      },
      set(value) {
        this.$store.dispatch('statistiques/setPeriode', value);
      }
    },

    chiffreAffairesChartData() {
      if (!this.chiffreAffaires.length) return { labels: [], datasets: [] };

      return {
        labels: this.chiffreAffaires.map(item => item.label),
        datasets: [
          {
            label: "Chiffre d'affaires (€)",
            data: this.chiffreAffaires.map(item => item.value),
            borderColor: '#4e73df',
            backgroundColor: 'rgba(78, 115, 223, 0.05)',
            pointBackgroundColor: '#4e73df',
            pointBorderColor: '#fff',
            pointHoverRadius: 3,
            pointHoverBackgroundColor: '#2e59d9',
            pointHoverBorderColor: '#fff',
            pointHitRadius: 10,
            pointBorderWidth: 2,
            fill: true,
            tension: 0.3
          }
        ]
      };
    },

    coursesCountChartData() {
      if (!this.coursesCount.length) return { labels: [], datasets: [] };

      return {
        labels: this.coursesCount.map(item => item.label),
        datasets: [
          {
            label: 'Nombre de courses',
            data: this.coursesCount.map(item => item.value),
            backgroundColor: '#1cc88a',
            borderColor: '#1cc88a',
            borderWidth: 1
          }
        ]
      };
    },

    repartitionTypeChartData() {
      if (!this.repartitionByType.length) return { labels: [], datasets: [] };

      const backgroundColors = ['#4e73df', '#1cc88a', '#36b9cc', '#f6c23e', '#e74a3b'];

      return {
        labels: this.repartitionByType.map(item => item.type),
        datasets: [
          {
            data: this.repartitionByType.map(item => item.count),
            backgroundColor: backgroundColors.slice(0, this.repartitionByType.length)
          }
        ]
      };
    },

    repartitionStatusChartData() {
      if (!this.repartitionByStatus.length) return { labels: [], datasets: [] };

      const statusColors = {
        'Terminé': '#1cc88a',
        'Annulé': '#e74a3b',
        'En cours': '#4e73df',
        'Planifié': '#f6c23e'
      };

      return {
        labels: this.repartitionByStatus.map(item => item.status),
        datasets: [
          {
            label: 'Courses',
            data: this.repartitionByStatus.map(item => item.count),
            backgroundColor: this.repartitionByStatus.map(item => statusColors[item.status] || '#6c757d')
          }
        ]
      };
    },

    paymentMethodChartData() {
      return {
        labels: this.paymentMethods.map(item => item.method),
        datasets: [
          {
            data: this.paymentMethods.map(item => item.count),
            backgroundColor: this.paymentMethods.map(item => item.color)
          }
        ]
      };
    }
  },
  methods: {
    ...mapActions('statistiques', ['fetchAllData']),

    isLoading(key) {
      return this.$store.getters['statistiques/isLoading'](key);
    },

    formatNumber(value) {
      return new Intl.NumberFormat('fr-FR').format(value || 0);
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0
      }).format(value || 0);
    }
  },
  created() {
    this.fetchAllData();
  }
}
</script>

<style scoped>
.chart-section {
  margin-bottom: 1.5rem;
}
</style>